import React, { useState } from "react";
import {
  MapPin,
  MessageSquare,
  Share2,
  Clock,
  Facebook,
  Linkedin,
  Instagram,
  Lock,
  ArrowRight,
} from "lucide-react";
import HeroBanner from "../../components/User/common/HeroBanner";
import emailjs from "@emailjs/browser";
import toast from 'react-hot-toast';

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
        <Icon className="text-white" size={24} />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 pt-2">{title}</h3>
    </div>
    <div className="text-gray-600">{children}</div>
  </div>
);

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

 const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.name || !formData.email || !formData.phone) {
    alert("Please fill all required fields");
    return;
  }

  try {
    setLoading(true);

   const response = await emailjs.send(
          "service_tnhy87m",
          "template_torrsx6",
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
          },
          {
            publicKey: "fBioAsHDz3ENgnZyB", 
          }
        );

    console.log("SUCCESS:", response);
    toast.success("Message sent successfully!");

    setFormData({
      name: "",
      email: "",
      phone: "",
      service: "",
      message: "",
    });

  } catch (error) {
    console.error("FAILED:", error);
    toast.error("Something went wrong. Try again.");
  } finally {
    setLoading(false);
  }
};

  const handleChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  return (
    <>
      <HeroBanner subheading="Home / Contact Us" />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 px-6 py-2 border-2 border-gray-900 rounded-full mb-8">
              <span className="w-2 h-2 bg-gray-900 rounded-full"></span>
              <span className="font-medium text-gray-900">
                Get in Touch With Us
              </span>
              <span className="w-2 h-2 bg-gray-900 rounded-full"></span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              <span className="italic font-serif">Your First Step</span>{" "}
              <span className="font-sans">Toward Strategic Growth</span>
            </h1>

            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              We're here to craft strategies and deliver creative marketing
              solutions. Share your details and our team will connect with you
              to spark measurable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start h-full">
            {/* Left Column - Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InfoCard icon={MapPin} title="Address.">
                <p className="leading-relaxed">
                  15442 Ventura BLVD STE 201-2193 SHERMAN OAKS, CA. US 91403
                </p>
              </InfoCard>

              <InfoCard icon={MessageSquare} title="Contact.">
                <p className="mb-2">+1 7705881660</p>
                <p className="text-gray-900">Info@quantrovia.com</p>
              </InfoCard>

              <InfoCard icon={Share2} title="Contact.">
                <p className="mb-4">Follow us for updates:</p>
                <div className="flex gap-3">
                  <button className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-700 transition-colors">
                    <Facebook size={20} />
                  </button>
                  <button className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-700 transition-colors">
                    <XIcon />
                  </button>
                  <button className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-700 transition-colors">
                    <Linkedin size={20} />
                  </button>
                  <button className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white hover:bg-gray-700 transition-colors">
                    <Instagram size={20} />
                  </button>
                </div>
              </InfoCard>

              <InfoCard icon={Clock} title="Working Hours.">
                <p className="mb-2">Mon – Fri: 9:00am – 5:00pm</p>
                <p>Closed on weekends.</p>
              </InfoCard>
            </div>

            {/* Right Column - Contact Form */}
            <div className="bg-white rounded-3xl border border-gray-200 p-8 shadow-sm py-9">
              <div className="space-y-6">
                {/* Grid Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Name*
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gray-900 focus:ring-0 transition-colors bg-transparent rounded-lg"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Email*
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="Enter Your Email"
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gray-900 focus:ring-0 transition-colors bg-transparent rounded-lg"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone*
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gray-900 focus:ring-0 transition-colors bg-transparent rounded-lg"
                    />
                  </div>

                  {/* Service */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Service
                    </label>
                    <select
                      value={formData.service}
                      onChange={(e) => handleChange("service", e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gray-900 focus:ring-0 transition-colors bg-transparent rounded-lg appearance-none cursor-pointer"
                    >
                      <option value="">Software Development</option>
                      <option value="Engineering Digital Solutions">
                        Engineering Digital Solutions
                      </option>
                      <option value="Construction Tech">
                        Construction Tech (Project Areas)
                      </option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Cloud & Infrastructure">
                        Cloud & Infrastructure (VDI Focus)
                      </option>
                      <option value="Data & AI">Data & AI Solutions</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-gray-900 focus:ring-0 transition-colors bg-transparent resize-none rounded-lg"
                  ></textarea>
                </div>

                {/* Footer */}
                <div className="flex flex-col md:flex-row gap-3 items-center justify-between pt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Lock size={16} />
                    <span>Your Information Is Confidential</span>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-gray-900 text-white px-8 py-4 rounded-xl font-semibold hover:bg-gray-800 transition-colors flex items-center gap-2 group disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send Your Message"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
